import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { format, addDays, isSameDay, startOfDay, endOfDay } from "date-fns";
import { de } from "date-fns/locale";
import { useLanguage } from "@/hooks/useLanguage";
import { Bell, Calendar as CalendarIcon, ArrowLeft, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

interface FollowUp {
  id: string;
  time: string;
  name?: string;
  address: string;
  notes?: string;
  source: 'running_list' | 'd2d_units';
}

interface KTFollowUpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (date: Date, notes?: string, customerEmail?: string) => void;
  unitId?: string;
  unitTags?: string[];
  initialEmail?: string;
}

export const KTFollowUpDialog = ({
  open,
  onOpenChange,
  onConfirm,
  unitId,
  unitTags = [],
  initialEmail = '',
}: KTFollowUpDialogProps) => {
  const { getLocale, isHoliday, getNextAvailableDay } = useLanguage();
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<'calendar' | 'day'>('calendar');
  const [selectedType, setSelectedType] = useState<'auto' | 'manual'>('auto');
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState('09:00');
  const [autoFollowUpDays, setAutoFollowUpDays] = useState('5');
  const [notes, setNotes] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [dayViewDate, setDayViewDate] = useState<Date>();
  const [existingFollowUps, setExistingFollowUps] = useState<FollowUp[]>([]);
  const [loadingFollowUps, setLoadingFollowUps] = useState(false);

  // Load saved customer email when unit is provided or use initialEmail
  useEffect(() => {
    // If initialEmail is provided, use it directly
    if (initialEmail) {
      setCustomerEmail(initialEmail);
      return;
    }
    
    const loadUnitEmail = async () => {
      if (!unitId || !user) return;
      
      try {
        const { data } = await supabase
          .from('d2d_units')
          .select('customer_email')
          .eq('id', unitId)
          .eq('user_id', user.id)
          .single();
        
        if (data?.customer_email) {
          setCustomerEmail(data.customer_email);
        }
      } catch (error) {
        console.error('Error loading customer email:', error);
      }
    };
    
    loadUnitEmail();
  }, [unitId, user, initialEmail]);

  const calculateAutoFollowUp = (days: number): Date => {
    const futureDate = addDays(new Date(), days);
    return getNextAvailableDay(futureDate);
  };

  const loadFollowUpsForDate = async (date: Date) => {
    if (!user) return;
    
    setLoadingFollowUps(true);
    try {
      const dayStart = startOfDay(date);
      const dayEnd = endOfDay(date);
      
      // Load from running_list_entries
      const { data: runningListData } = await supabase
        .from('running_list_entries')
        .select('id, street, house_number, apartment_unit, follow_up_scheduled, notes')
        .eq('user_id', user.id)
        .gte('follow_up_scheduled', dayStart.toISOString())
        .lte('follow_up_scheduled', dayEnd.toISOString())
        .not('follow_up_scheduled', 'is', null);

      // Load from d2d_units
      const { data: d2dUnitsData } = await supabase
        .from('d2d_units')
        .select(`
          id, 
          unit_name, 
          next_reminder, 
          follow_up_notes,
          d2d_houses!inner(
            house_number,
            d2d_streets!inner(
              name,
              plz
            )
          )
        `)
        .eq('user_id', user.id)
        .gte('next_reminder', dayStart.toISOString())
        .lte('next_reminder', dayEnd.toISOString())
        .not('next_reminder', 'is', null);

      const followUps: FollowUp[] = [];

      // Process running list entries
      if (runningListData) {
        runningListData.forEach(entry => {
          const time = format(new Date(entry.follow_up_scheduled), 'HH:mm', { locale: getLocale() });
          const address = `${entry.street} ${entry.house_number}`;
          const name = entry.apartment_unit || `${entry.street} ${entry.house_number}`;
          followUps.push({
            id: `rl_${entry.id}`,
            time,
            name,
            address,
            notes: entry.notes,
            source: 'running_list'
          });
        });
      }

      // Process d2d units
      if (d2dUnitsData) {
        d2dUnitsData.forEach(unit => {
          const time = format(new Date(unit.next_reminder), 'HH:mm', { locale: getLocale() });
          const address = `${unit.d2d_houses.d2d_streets.name} ${unit.d2d_houses.house_number}`;
          const name = unit.unit_name || `${unit.d2d_houses.d2d_streets.name} ${unit.d2d_houses.house_number}`;
          followUps.push({
            id: `d2d_${unit.id}`,
            time,
            name,
            address,
            notes: unit.follow_up_notes,
            source: 'd2d_units'
          });
        });
      }

      // Sort by address (street + house number) first, then by time
      followUps.sort((a, b) => {
        const getAddressParts = (address: string) => {
          const parts = address.split(' ');
          const houseNumber = parts[parts.length - 1];
          const street = parts.slice(0, -1).join(' ');
          return { street, houseNumber: parseInt(houseNumber) || 0 };
        };
        
        const aAddress = getAddressParts(a.address);
        const bAddress = getAddressParts(b.address);
        
        if (aAddress.street !== bAddress.street) {
          return aAddress.street.localeCompare(bAddress.street);
        }
        
        if (aAddress.houseNumber !== bAddress.houseNumber) {
          return aAddress.houseNumber - bAddress.houseNumber;
        }
        
        return a.time.localeCompare(b.time);
      });
      
      setExistingFollowUps(followUps);
    } catch (error) {
      console.error('Error loading follow-ups:', error);
    } finally {
      setLoadingFollowUps(false);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setDayViewDate(date);
      setSelectedDate(date);
      setViewMode('day');
      loadFollowUpsForDate(date);
    }
  };

  const handleBackToCalendar = () => {
    setViewMode('calendar');
    setDayViewDate(undefined);
  };

  // Follow-up time slots every 10 minutes: 
  // Monday-Friday: 10:00-13:00 and 15:30-19:30
  // Saturday: 10:00-16:15
  const generateFollowUpTimeSlots = () => {
    const slots: string[] = [];
    
    const isSaturday = dayViewDate && dayViewDate.getDay() === 6;
    
    if (isSaturday) {
      for (let hour = 10; hour <= 16; hour++) {
        for (let minute = 0; minute < 60; minute += 10) {
          if (hour === 16 && minute > 15) break;
          const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          slots.push(timeSlot);
        }
      }
    } else {
      for (let hour = 10; hour <= 13; hour++) {
        for (let minute = 0; minute < 60; minute += 10) {
          if (hour === 13 && minute > 0) break;
          const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          slots.push(timeSlot);
        }
      }
      
      for (let hour = 15; hour <= 19; hour++) {
        const startMinute = hour === 15 ? 30 : 0;
        for (let minute = startMinute; minute < 60; minute += 10) {
          if (hour === 19 && minute > 30) break;
          const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          slots.push(timeSlot);
        }
      }
    }
    
    return slots;
  };

  const followUpTimeSlots = generateFollowUpTimeSlots();

  const isTimeSlotTaken = (timeSlot: string) => {
    return existingFollowUps.some(followUp => followUp.time === timeSlot);
  };

  // Check if a time slot has already been attempted for NA follow-ups
  const isTimeSlotAttempted = (timeSlot: string) => {
    if (!dayViewDate || !unitTags.length) return false;
    
    const selectedDow = dayViewDate.getDay();
    const [hours] = timeSlot.split(':').map(Number);
    
    // Determine which day-specific time slot this corresponds to
    let targetSlot: string | null = null;
    
    switch (selectedDow) {
      case 1: // Monday
        if (hours >= 15 && hours < 20) targetSlot = 'monday-afternoon';
        break;
      case 2: // Tuesday
        if (hours >= 10 && hours < 13) targetSlot = 'tuesday-morning';
        else if (hours >= 15 && hours < 20) targetSlot = 'tuesday-afternoon';
        break;
      case 3: // Wednesday
        if (hours >= 10 && hours < 13) targetSlot = 'wednesday-morning';
        break;
      case 4: // Thursday
        if (hours >= 10 && hours < 13) targetSlot = 'thursday-morning';
        else if (hours >= 15 && hours < 20) targetSlot = 'thursday-afternoon';
        break;
      case 5: // Friday
        if (hours >= 10 && hours < 13) targetSlot = 'friday-morning';
        else if (hours >= 15 && hours < 20) targetSlot = 'friday-afternoon';
        break;
      case 6: // Saturday
        if (hours >= 10 && hours < 13) targetSlot = 'saturday-morning';
        else if (hours >= 14 && hours < 17) targetSlot = 'saturday-afternoon';
        break;
    }
    
    if (!targetSlot) return false;
    
    // Check if this time slot was already attempted (NA tag with matching time)
    return unitTags.some(tag => {
      if (!tag.startsWith('NA-')) return false;
      
      const timestampMatch = tag.match(/NA-(\d{8})-(\d{4})/);
      if (!timestampMatch) return false;
      
      const dateStr = timestampMatch[1];
      const timeStr = timestampMatch[2];
      const year = parseInt(dateStr.substring(0, 4));
      const month = parseInt(dateStr.substring(4, 6)) - 1;
      const day = parseInt(dateStr.substring(6, 8));
      const attemptHour = parseInt(timeStr.substring(0, 2));
      const attemptDow = new Date(year, month, day).getDay();
      
      // Determine which slot this attempt was in
      let attemptSlot: string | null = null;
      
      switch (attemptDow) {
        case 1:
          if (attemptHour >= 15 && attemptHour < 20) attemptSlot = 'monday-afternoon';
          break;
        case 2:
          if (attemptHour >= 10 && attemptHour < 13) attemptSlot = 'tuesday-morning';
          else if (attemptHour >= 15 && attemptHour < 20) attemptSlot = 'tuesday-afternoon';
          break;
        case 3:
          if (attemptHour >= 10 && attemptHour < 13) attemptSlot = 'wednesday-morning';
          break;
        case 4:
          if (attemptHour >= 10 && attemptHour < 13) attemptSlot = 'thursday-morning';
          else if (attemptHour >= 15 && attemptHour < 20) attemptSlot = 'thursday-afternoon';
          break;
        case 5:
          if (attemptHour >= 10 && attemptHour < 13) attemptSlot = 'friday-morning';
          else if (attemptHour >= 15 && attemptHour < 20) attemptSlot = 'friday-afternoon';
          break;
        case 6:
          if (attemptHour >= 10 && attemptHour < 13) attemptSlot = 'saturday-morning';
          else if (attemptHour >= 14 && attemptHour < 17) attemptSlot = 'saturday-afternoon';
          break;
      }
      
      return attemptSlot === targetSlot;
    });
  };

  const getAvailableTimeSlots = () => {
    return followUpTimeSlots.filter(timeSlot => !isTimeSlotTaken(timeSlot));
  };

  const handleConfirm = async () => {
    setCreating(true);
    try {
      let finalDate: Date;
      
      if (selectedType === 'auto') {
        finalDate = calculateAutoFollowUp(parseInt(autoFollowUpDays));
      } else {
        if (!selectedDate) {
          return;
        }
        const [hours, minutes] = selectedTime.split(':');
        finalDate = new Date(selectedDate);
        finalDate.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0, 0);
      }
      
      onConfirm(finalDate, notes.trim() || undefined, customerEmail.trim() || undefined);
      
      // Reset form
      setSelectedType('auto');
      setSelectedDate(undefined);
      setSelectedTime('09:00');
      setAutoFollowUpDays('5');
      setNotes('');
      setCustomerEmail('');
      onOpenChange(false);
    } finally {
      setCreating(false);
    }
  };

  const isConfirmDisabled = (selectedType === 'manual' && !selectedDate) || creating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            Follow-Up konfigurieren
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
            
            {/* Typ Auswahl */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Follow-up Typ wählen</Label>
              <div className="grid grid-cols-2 gap-3">
                <Button
                  variant={selectedType === 'auto' ? 'default' : 'outline'}
                  onClick={() => setSelectedType('auto')}
                  className="h-12 flex-col gap-1 text-sm font-medium"
                >
                  <Clock className="h-4 w-4" />
                  Automatisch
                </Button>
                <Button
                  variant={selectedType === 'manual' ? 'default' : 'outline'}
                  onClick={() => setSelectedType('manual')}
                  className="h-12 flex-col gap-1 text-sm font-medium"
                >
                  <CalendarIcon className="h-4 w-4" />
                  Manuell
                </Button>
              </div>
            </div>

            {/* Automatische Konfiguration */}
            {selectedType === 'auto' && (
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Zeitraum auswählen</Label>
                  <Select value={autoFollowUpDays} onValueChange={setAutoFollowUpDays}>
                    <SelectTrigger className="h-11 mt-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">In 3 Tagen</SelectItem>
                      <SelectItem value="4">In 4 Tagen</SelectItem>
                      <SelectItem value="5">In 5 Tagen</SelectItem>
                      <SelectItem value="6">In 6 Tagen</SelectItem>
                      <SelectItem value="7">In 7 Tagen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="bg-muted/30 border rounded-lg p-4">
                  <div className="flex items-center gap-2 text-muted-foreground font-medium mb-1">
                    <CalendarIcon className="h-4 w-4" />
                    Geplantes Datum
                  </div>
                  <div className="text-foreground font-semibold text-lg">
                    {format(calculateAutoFollowUp(parseInt(autoFollowUpDays)), 'EEEE, dd. MMMM yyyy, HH:mm', { locale: getLocale() })} Uhr
                  </div>
                </div>
              </div>
            )}

            {/* Manueller Kalender */}
            {selectedType === 'manual' && (
              <div className="space-y-4">
                {viewMode === 'calendar' ? (
                  <div className="space-y-4">
                    <div className="bg-card rounded-lg p-4 border flex justify-center">
                      <Calendar 
                        mode="single" 
                        selected={selectedDate} 
                        onSelect={handleDateSelect} 
                        disabled={(date) => date < startOfDay(new Date()) || isHoliday(date)}
                        initialFocus 
                        className="pointer-events-auto"
                        classNames={{
                          months: "flex justify-center",
                          month: "space-y-4",
                          table: "w-auto border-collapse mx-auto",
                          head_row: "flex",
                          head_cell: "w-9 text-center font-medium text-sm",
                          row: "flex mt-2",
                          cell: "w-9 h-9 text-center p-0",
                          day: "w-9 h-9 text-sm font-medium hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                          day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                          day_today: "bg-accent text-accent-foreground",
                          day_outside: "text-muted-foreground opacity-50",
                          day_disabled: "text-muted-foreground opacity-50 cursor-not-allowed",
                          day_hidden: "invisible"
                        }}
                        showOutsideDays={false}
                        components={{
                          Dropdown: ({ name, value, onChange }) => {
                            const months = Array.from({ length: 12 }, (_, i) => ({
                              value: i.toString(),
                              label: format(new Date(2000, i), 'MMMM', { locale: getLocale() })
                            }));
                            
                            const years = Array.from({ length: 10 }, (_, i) => {
                              const year = new Date().getFullYear() + i;
                              return { value: year.toString(), label: year.toString() };
                            });
                            
                            const options = name === 'months' ? months : years;
                            
                            const handleValueChange = (newValue: string) => {
                              const event = {
                                target: { value: newValue },
                                currentTarget: { value: newValue }
                              } as React.ChangeEvent<HTMLSelectElement>;
                              onChange?.(event);
                            };
                            
                            return (
                              <Select value={value?.toString()} onValueChange={handleValueChange}>
                                <SelectTrigger className="w-auto h-8 px-2 text-sm font-medium">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="max-h-60">
                                  {options.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleBackToCalendar}
                        className="flex items-center gap-2"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        Zurück
                      </Button>
                      <h4 className="font-semibold text-base">
                        {dayViewDate && format(dayViewDate, 'dd.MM.yyyy', { locale: getLocale() })}
                      </h4>
                    </div>

                    <div className="space-y-4">
                      {/* Geplante Follow-ups */}
                      <div>
                        <h5 className="font-medium text-sm mb-3 text-muted-foreground">Geplante Termine</h5>
                        
                        {loadingFollowUps ? (
                          <div className="flex items-center justify-center py-8 text-muted-foreground">
                            <Clock className="animate-spin h-4 w-4 mr-2" />
                            Lade Termine...
                          </div>
                        ) : existingFollowUps.length === 0 ? (
                          <div className="text-center text-muted-foreground py-6 text-sm bg-muted/30 rounded-lg">
                            Keine Termine geplant
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {existingFollowUps.map((followUp) => (
                              <div key={followUp.id} className="bg-card border rounded-lg p-3">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-sm truncate">
                                      {followUp.name}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">
                                      {followUp.address}
                                    </div>
                                  </div>
                                  <div className="text-sm font-medium ml-2 flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {followUp.time}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Verfügbare Zeiten */}
                      <div>
                        <h5 className="font-medium text-sm mb-3 text-muted-foreground">Verfügbare Zeiten</h5>
                        {getAvailableTimeSlots().length === 0 ? (
                          <div className="text-center text-muted-foreground py-6 text-sm bg-muted/30 rounded-lg">
                            Keine freien Zeiten
                          </div>
                        ) : (
                          <div className="grid grid-cols-4 gap-2 max-h-60 overflow-y-auto">
                            {getAvailableTimeSlots().map((timeSlot) => {
                              const isAttempted = isTimeSlotAttempted(timeSlot);
                              return (
                                <Button
                                  key={timeSlot}
                                  variant={selectedTime === timeSlot ? "default" : "outline"}
                                  size="sm"
                                  className={cn(
                                    "text-xs h-8",
                                    isAttempted && "opacity-50 cursor-not-allowed line-through"
                                  )}
                                  onClick={() => !isAttempted && setSelectedTime(timeSlot)}
                                  disabled={isAttempted}
                                  title={isAttempted ? "Bereits versucht (NA)" : undefined}
                                >
                                  {timeSlot}
                                </Button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* E-Mail für Terminbestätigung */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">E-Mail für Terminbestätigung</Label>
              <Input 
                type="email"
                value={customerEmail} 
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="kunde@example.com"
                className="h-10"
              />
              <p className="text-xs text-muted-foreground">
                Eine Terminbestätigung mit Kalendereintrag wird an diese Adresse gesendet
              </p>
            </div>

            {/* Notizen */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Follow-Up Notizen</Label>
              <Textarea 
                value={notes} 
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Zusätzliche Informationen zum Follow-up..."
                className="min-h-[80px] resize-none"
              />
            </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="flex-1 h-9 text-sm"
              disabled={creating}
            >
              Abbrechen
            </Button>
            <Button 
              onClick={handleConfirm}
              disabled={isConfirmDisabled}
              className="flex-1 h-9 font-medium text-sm"
            >
              {creating ? (
                <>
                  <Clock className="animate-spin h-4 w-4 mr-2" />
                  Erstelle...
                </>
              ) : (
                'Erstellen'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};